import React, { useState, useEffect, useMemo } from 'react';
import { collection, addDoc, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, writeBatch, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    CalendarIcon, EditIcon, StarIcon, TrashIcon, SearchIcon, XIcon, 
    CheckCircleIcon, ChatAltIcon, FilterIcon 
} from '../../components/icons/Icons';

// Helper: Generates Avatar URL or falls back to Initials if photoURL is missing/null
const getAvatar = (name, url) => {
    if (url && url.length > 5) return url; // Basic validation
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=random&color=fff&size=128`;
};

export default function PeerLearningHub({ userProfile, appId }) {
    const [sessions, setSessions] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [attendanceSession, setAttendanceSession] = useState(null);
    const [feedbackSession, setFeedbackSession] = useState(null);
    const [viewFeedbackSession, setViewFeedbackSession] = useState(null);

    // --- FILTER STATES ---
    const [timeFilter, setTimeFilter] = useState('30days');
    const [deptFilter, setDeptFilter] = useState('All');
    
    // --- PERMISSIONS ---
const [isManager, setIsManager] = useState(userProfile.role === 'Admin');

    useEffect(() => {
        const checkManagerStatus = async () => {
            // Admins are always managers
            if (userProfile.role === 'Admin') {
                setIsManager(true);
                return;
            }

            try {
                // Check if ANY user has this person set as their manager
                const usersRef = collection(db, `artifacts/${appId}/users`);
                const q = query(
                    usersRef, 
                    where("managerId", "==", userProfile.id), 
                    limit(1) // We only need to find one to prove they are a manager
                );
                const snapshot = await getDocs(q);
                setIsManager(!snapshot.empty);
            } catch (error) {
                console.error("Error checking manager status:", error);
                setIsManager(false);
            }
        };

        checkManagerStatus();
    }, [appId, userProfile.id, userProfile.role]);
    const isPresenter = (session) => session.presenterIds?.includes(userProfile.id);

    // --- LOAD SESSIONS ---
    const fetchSessions = async () => {
        try {
            const ref = collection(db, `artifacts/${appId}/pd_events`);
            const q = query(ref, where('category', '==', 'Peer Learning'), orderBy('date', 'desc'));
            const snap = await getDocs(q);
            setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Error fetching sessions:", error);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, [appId]);

    // --- FILTER LOGIC ---
    const filteredSessions = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);

        return sessions.filter(session => {
            const sessionDate = new Date(session.date.seconds * 1000);
            
            // 1. Department Filter
            if (deptFilter !== 'All' && session.organizerDept !== deptFilter) {
                return false;
            }

            // 2. Time Filter
            if (timeFilter === '30days') {
                return sessionDate >= now && sessionDate <= thirtyDaysFromNow;
            }
            if (timeFilter === 'upcoming') {
                return sessionDate >= now;
            }
            if (timeFilter === 'past') {
                return sessionDate < now;
            }
            return true; // 'all'
        });
    }, [sessions, timeFilter, deptFilter]);

    const uniqueDepts = useMemo(() => {
        const depts = new Set(sessions.map(s => s.organizerDept).filter(Boolean));
        return ['All', ...Array.from(depts)];
    }, [sessions]);

    // --- ACTIONS ---

    // ✅ FIXED: Cascade Delete
    // Deletes the Event AND all associated Activity Logs for attendees
    const handleDelete = async (sessionId) => {
        if (!window.confirm("Are you sure? This will delete the session AND remove it from all attendees' dashboards.")) return;

        try {
            const batch = writeBatch(db);

            // 1. Delete the Event Document
            const eventRef = doc(db, `artifacts/${appId}/pd_events`, sessionId);
            batch.delete(eventRef);

            // 2. Find and Delete all linked Activity Logs
            const activitiesRef = collection(db, `artifacts/${appId}/pd_activities`);
            const q = query(activitiesRef, where('linkedEventId', '==', sessionId));
            const querySnapshot = await getDocs(q);

            querySnapshot.forEach((doc) => {
                batch.delete(doc.ref);
            });

            // 3. Commit Batch
            await batch.commit();
            
            // 4. Refresh UI
            fetchSessions();
        } catch (error) {
            console.error("Error deleting session:", error);
            alert("Failed to delete session records.");
        }
    };

    const handleModalClose = () => {
        setIsCreateModalOpen(false);
        setEditingSession(null);
        fetchSessions();
    };

    const isAttendee = (session) => session.attendeeIds?.includes(userProfile.id);
    const hasGivenFeedback = (session) => session.feedbackLog?.[userProfile.id];

    return (
        <div>
            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h3 className="text-lg font-bold text-[#213368]">Peer Learning Sessions</h3>
                    <p className="text-sm text-gray-500">Share knowledge, grow together.</p>
                </div>
                {isManager && (
                    <button 
                        onClick={() => setIsCreateModalOpen(true)}
                        className="px-4 py-2 bg-[#20BEC6] text-white text-sm font-bold rounded-lg hover:opacity-90 flex items-center shadow-sm whitespace-nowrap"
                    >
                        <span className="mr-1 text-lg">+</span> Create Session
                    </button>
                )}
            </div>

            {/* FILTER BAR */}
            <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex items-center space-x-2 w-full md:w-auto overflow-x-auto">
                    <FilterButton active={timeFilter === '30days'} onClick={() => setTimeFilter('30days')} label="Next 30 Days" />
                    <FilterButton active={timeFilter === 'upcoming'} onClick={() => setTimeFilter('upcoming')} label="All Upcoming" />
                    <FilterButton active={timeFilter === 'past'} onClick={() => setTimeFilter('past')} label="Past" />
                    <FilterButton active={timeFilter === 'all'} onClick={() => setTimeFilter('all')} label="All Time" />
                </div>

                <div className="flex items-center space-x-2 w-full md:w-auto">
                    <span className="text-xs font-bold text-gray-400 uppercase">Filter Dept:</span>
                    <select 
                        value={deptFilter}
                        onChange={(e) => setDeptFilter(e.target.value)}
                        className="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-[#20BEC6] focus:border-[#20BEC6] block p-2"
                    >
                        {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                </div>
            </div>

            {/* SESSION LIST */}
            <div className="space-y-4">
                {filteredSessions.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <p className="text-gray-400 italic">No sessions found matching your filters.</p>
                        <button onClick={() => {setTimeFilter('all'); setDeptFilter('All')}} className="mt-2 text-[#20BEC6] font-bold text-sm">Clear Filters</button>
                    </div>
                )}
                
                {filteredSessions.map(session => (
                    <SessionCard 
                        key={session.id}
                        session={session}
                        isManager={isManager}
                        isPresenter={isPresenter(session)}
                        isAttendee={isAttendee(session)}
                        hasGivenFeedback={hasGivenFeedback(session)}
                        onEdit={() => { setEditingSession(session); setIsCreateModalOpen(true); }}
                        onDelete={() => handleDelete(session.id)}
                        onMarkAttendance={() => setAttendanceSession(session)}
                        onGiveFeedback={() => setFeedbackSession(session)}
                        onViewFeedback={() => setViewFeedbackSession(session)}
                    />
                ))}
            </div>

            {/* --- MODALS --- */}
            {isCreateModalOpen && (
                <CreateEditSessionModal 
                    appId={appId} 
                    onClose={handleModalClose} 
                    currentUser={userProfile}
                    existingData={editingSession}
                />
            )}
            {attendanceSession && (
                <AttendanceModal 
                    appId={appId} 
                    session={attendanceSession} 
                    onClose={() => { setAttendanceSession(null); fetchSessions(); }} 
                />
            )}
            {feedbackSession && (
                <FeedbackModal 
                    appId={appId} 
                    session={feedbackSession} 
                    userId={userProfile.id}
                    onClose={() => { setFeedbackSession(null); fetchSessions(); }} 
                />
            )}
            {viewFeedbackSession && (
                <ViewFeedbackModal 
                    session={viewFeedbackSession}
                    onClose={() => setViewFeedbackSession(null)}
                />
            )}
        </div>
    );
}

function FilterButton({ active, onClick, label }) {
    return (
        <button 
            onClick={onClick}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                active 
                ? 'bg-[#213368] text-white shadow-md' 
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
        >
            {label}
        </button>
    );
}

// ------------------------------------------------------------------
// SESSION CARD
// ------------------------------------------------------------------
function SessionCard({ session, isManager, isPresenter, isAttendee, hasGivenFeedback, onEdit, onDelete, onMarkAttendance, onGiveFeedback, onViewFeedback }) {
    const isUpcoming = new Date(session.date.seconds * 1000) > new Date();
    const feedbackCount = session.feedbackLog ? Object.keys(session.feedbackLog).length : 0;

    return (
        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm hover:shadow-md transition group relative overflow-hidden">
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isUpcoming ? 'bg-[#20BEC6]' : 'bg-gray-300'}`}></div>

            <div className="flex flex-col md:flex-row justify-between pl-2">
                <div className="mb-4 md:mb-0">
                    {/* Title & Status */}
                    <div className="flex items-center space-x-3 mb-1">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                            isUpcoming ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                            {isUpcoming ? 'Upcoming' : 'Completed'}
                        </span>
                        <h4 className="font-bold text-[#213368] text-lg">{session.title}</h4>
                    </div>
                    
                    {/* Date & Time */}
                    <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-500 mt-2 space-y-1 sm:space-y-0 sm:space-x-4">
                        <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1.5 text-gray-400"/>
                            {new Date(session.date.seconds * 1000).toLocaleDateString()}
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span>{session.durationHours} Hours</span>
                    </div>

                    {/* Presenters (WITH AVATARS) */}
                    <div className="mt-3 text-sm text-gray-600 flex items-center flex-wrap">
                        <span className="font-medium text-gray-700 mr-2">Presenters:</span>
                        {session.presenterNames?.length > 0 ? (
                            session.presenterNames.map((name, i) => {
                                // Safe access to photo array
                                const photo = session.presenterPhotos && session.presenterPhotos[i];
                                return (
                                    <span key={i} className="inline-flex items-center bg-blue-50 text-blue-700 pl-1 pr-2 py-0.5 rounded-full text-xs mr-2 mb-1 border border-blue-100">
                                        <img 
                                            src={getAvatar(name, photo)} 
                                            alt={name}
                                            className="w-5 h-5 rounded-full mr-1.5 object-cover"
                                            onError={(e) => {e.target.onerror = null; e.target.src = getAvatar(name, null)}}
                                        />
                                        {name}
                                    </span>
                                );
                            })
                        ) : <span className="text-gray-400 italic">TBD</span>}
                    </div>

                    {/* Organizer Info (WITH AVATAR) */}
                    <div className="mt-3 flex items-center text-xs text-gray-400">
                        <img 
                            src={getAvatar(session.organizerName, session.organizerPhotoURL)} 
                            alt={session.organizerName || 'Org'}
                            className="h-5 w-5 rounded-full mr-2 object-cover border border-gray-200"
                            onError={(e) => {e.target.onerror = null; e.target.src = getAvatar(session.organizerName, null)}}
                        />
                        <span>
                            Organized by <span className="font-medium text-gray-600">{session.organizerName || 'Unknown'}</span> 
                            <span className="mx-1">•</span> {session.organizerDept || 'General'}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col items-end space-y-2">
                    {isManager && (
                        <div className="flex space-x-2">
                             <button onClick={onMarkAttendance} className="text-xs font-bold text-white bg-[#213368] hover:bg-opacity-90 px-3 py-1.5 rounded-lg transition shadow-sm">
                                Attendance ({session.attendeeIds?.length || 0})
                            </button>
                            <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"><EditIcon className="h-4 w-4" /></button>
                            <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"><TrashIcon className="h-4 w-4" /></button>
                        </div>
                    )}

                    {(isManager || isPresenter) && feedbackCount > 0 && (
                        <button onClick={onViewFeedback} className="mt-1 text-xs font-medium text-purple-600 hover:bg-purple-50 px-3 py-1.5 rounded-lg border border-purple-200 flex items-center transition">
                            <ChatAltIcon className="h-3 w-3 mr-1" /> View Feedback ({feedbackCount})
                        </button>
                    )}

                    {isAttendee && !hasGivenFeedback && (
                        <button onClick={onGiveFeedback} className="mt-2 text-xs font-medium text-orange-600 hover:bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200 flex items-center transition">
                            <StarIcon className="h-3 w-3 mr-1" /> Rate Session
                        </button>
                    )}
                    
                    {hasGivenFeedback && (
                        <span className="mt-2 text-xs text-green-600 font-medium px-3 py-1.5 bg-green-50 rounded-lg flex items-center">
                            <CheckCircleIcon className="h-3 w-3 mr-1"/> Feedback Sent
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------
// CREATE/EDIT MODAL
// ------------------------------------------------------------------
function CreateEditSessionModal({ appId, onClose, currentUser, existingData }) {
    const [formData, setFormData] = useState({ 
        title: existingData?.title || '', 
        date: existingData?.date ? new Date(existingData.date.seconds * 1000).toISOString().split('T')[0] : '', 
        duration: existingData?.durationHours || 1, 
    });
    
    // Initialize selected presenters with photos if available
    const [selectedPresenters, setSelectedPresenters] = useState(
        existingData 
        ? existingData.presenterIds.map((id, index) => ({ 
            id, 
            name: existingData.presenterNames[index],
            photoURL: existingData.presenterPhotos?.[index]
          })) 
        : []
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            const q = query(collection(db, `artifacts/${appId}/users`), where("status", "==", "Approved"));
            const snap = await getDocs(q);
            setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchUsers();
    }, [appId]);

    const addPresenter = (user) => {
        if (!selectedPresenters.find(p => p.id === user.id)) {
            // SNAPSHOT PHOTO HERE
            setSelectedPresenters([...selectedPresenters, { 
                id: user.id, 
                name: user.name, 
                photoURL: user.photoURL 
            }]);
        }
        setSearchTerm('');
    };

    const removePresenter = (userId) => {
        setSelectedPresenters(selectedPresenters.filter(p => p.id !== userId));
    };

    const suggestions = allUsers.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !selectedPresenters.find(p => p.id === u.id)
    ).slice(0, 5);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                category: 'Peer Learning',
                title: formData.title,
                date: new Date(formData.date),
                durationHours: Number(formData.duration),
                
                // Save/Update Organizer Info
                organizerId: existingData?.organizerId || currentUser.id,
                organizerName: existingData?.organizerName || currentUser.name,
                organizerDept: existingData?.organizerDept || currentUser.department || 'Unassigned',
                // Important: If editing, keep existing photo unless we want to force update. 
                // For new, use current user photo.
                organizerPhotoURL: existingData?.organizerPhotoURL || currentUser.photoURL || null,
                
                presenterIds: selectedPresenters.map(p => p.id),
                presenterNames: selectedPresenters.map(p => p.name),
                presenterPhotos: selectedPresenters.map(p => p.photoURL || null),
            };

            if (existingData) {
                await updateDoc(doc(db, `artifacts/${appId}/pd_events`, existingData.id), payload);
            } else {
                await addDoc(collection(db, `artifacts/${appId}/pd_events`), {
                    ...payload,
                    attendeeIds: [],
                    createdAt: serverTimestamp()
                });
            }
            onClose();
        } catch (err) {
            console.error(err);
            alert("Error saving session");
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-[#213368]">{existingData ? 'Edit Session' : 'Schedule Peer Session'}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XIcon className="h-6 w-6"/></button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Session Title</label>
                        <input className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none" 
                            placeholder="e.g. Intro to GraphQL" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                    </div>
                    <div className="flex space-x-4">
                        <div className="flex-1">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                            <input className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none" 
                                type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                        </div>
                        <div className="w-1/3">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Duration (Hrs)</label>
                            <input className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none" 
                                type="number" step="0.5" value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} required />
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Presenters</label>
                        <div className="relative">
                            <div className="flex items-center border border-gray-300 rounded-lg p-2.5 bg-gray-50 focus-within:ring-2 focus-within:ring-[#20BEC6] focus-within:bg-white transition">
                                <SearchIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <input className="bg-transparent outline-none w-full text-sm" placeholder="Search user name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            {searchTerm && suggestions.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {suggestions.map(u => (
                                        <div key={u.id} onClick={() => addPresenter(u)} className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 text-sm flex justify-between items-center">
                                            <div className="flex items-center">
                                                <img src={getAvatar(u.name, u.photoURL)} alt="" className="w-6 h-6 rounded-full mr-2 object-cover"/>
                                                <span className="font-medium text-gray-700">{u.name}</span>
                                            </div>
                                            <span className="text-xs text-gray-400">{u.department}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {selectedPresenters.map(p => (
                                <span key={p.id} className="inline-flex items-center pl-1 pr-2 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    <img src={getAvatar(p.name, p.photoURL)} alt="" className="w-5 h-5 rounded-full mr-1.5 object-cover"/>
                                    {p.name}
                                    <button type="button" onClick={() => removePresenter(p.id)} className="ml-2 text-blue-600 hover:text-blue-900 focus:outline-none">×</button>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-6 py-2.5 bg-[#20BEC6] text-white rounded-lg font-bold hover:opacity-90 transition shadow-sm">
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------
// ATTENDANCE MODAL (No changes needed, code is robust)
// ------------------------------------------------------------------
function AttendanceModal({ appId, session, onClose }) {
    const [departments, setDepartments] = useState({});
    const [selectedAttendees, setSelectedAttendees] = useState(new Set(session.attendeeIds || []));
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchUsers = async () => {
            const q = query(collection(db, `artifacts/${appId}/users`), where("status", "==", "Approved"));
            const snap = await getDocs(q);
            const groups = {};
            snap.forEach(doc => {
                const u = { id: doc.id, ...doc.data() };
                const dept = u.department || 'Unassigned';
                if (!groups[dept]) groups[dept] = [];
                groups[dept].push(u);
            });
            setDepartments(groups);
        };
        fetchUsers();
    }, [appId]);

    const toggleUser = (userId) => {
        const newSet = new Set(selectedAttendees);
        if (newSet.has(userId)) newSet.delete(userId);
        else newSet.add(userId);
        setSelectedAttendees(newSet);
    };

    const toggleDepartment = (deptName) => {
        const deptUsers = departments[deptName];
        const allSelected = deptUsers.every(u => selectedAttendees.has(u.id));
        const newSet = new Set(selectedAttendees);
        if (allSelected) {
            deptUsers.forEach(u => newSet.delete(u.id));
        } else {
            deptUsers.forEach(u => newSet.add(u.id));
        }
        setSelectedAttendees(newSet);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const finalAttendees = Array.from(selectedAttendees);
            const batch = writeBatch(db);
            const allUsers = Object.values(departments).flat();

            const sessionRef = doc(db, `artifacts/${appId}/pd_events`, session.id);
            batch.update(sessionRef, { attendeeIds: finalAttendees });

            finalAttendees.forEach(userId => {
                const userObj = allUsers.find(u => u.id === userId);
		const activityRef = doc(db, `artifacts/${appId}/pd_activities`, `${session.id}_${userId}`);
                batch.set(activityRef, {
                    userId: userId,
                    managerId: userObj?.managerId || null,
		    type: 'Peer Learning',
                    title: session.title,
                    status: 'Completed',
                    hours: Number(session.durationHours),
                    linkedEventId: session.id,
                    date: session.date,
                    createdAt: serverTimestamp(),
                }, { merge: true });
            });

            await batch.commit();
            setIsSaving(false);
            onClose();
        } catch (err) {
            console.error(err);
            alert("Error saving attendance");
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                    <div><h3 className="text-xl font-bold text-[#213368]">Attendance Sheet</h3><p className="text-sm text-gray-500 mt-1">{session.title}</p></div>
                    <div className="text-right"><span className="text-2xl font-bold text-[#20BEC6]">{selectedAttendees.size}</span><p className="text-xs text-gray-500 uppercase font-bold">Present</p></div>
                </div>
                <div className="flex-1 overflow-y-auto p-6 bg-white">
                    <input className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-[#20BEC6] outline-none text-sm bg-gray-50 mb-6" placeholder="Filter employees by name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Object.entries(departments).map(([dept, users]) => {
                            const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()));
                            if (filteredUsers.length === 0) return null;
                            const isSelected = users.every(u => selectedAttendees.has(u.id));
                            return (
                                <div key={dept} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm h-fit">
                                    <div className="bg-gray-100 px-4 py-3 flex justify-between items-center border-b border-gray-200">
                                        <h4 className="font-bold text-gray-700 text-sm">{dept}</h4>
                                        <button onClick={() => toggleDepartment(dept)} className="text-xs font-bold text-blue-600 hover:text-blue-800">{isSelected ? 'Deselect All' : 'Select All'}</button>
                                    </div>
                                    <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                                        {filteredUsers.map(u => (
                                            <div key={u.id} onClick={() => toggleUser(u.id)} className={`px-4 py-2.5 flex justify-between items-center cursor-pointer transition hover:bg-gray-50 ${selectedAttendees.has(u.id) ? 'bg-blue-50/50' : ''}`}>
                                                <div className="flex items-center">
                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 ${selectedAttendees.has(u.id) ? 'bg-[#20BEC6] border-[#20BEC6]' : 'border-gray-300'}`}>
                                                        {selectedAttendees.has(u.id) && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>}
                                                    </div>
                                                    <span className={`text-sm ${selectedAttendees.has(u.id) ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{u.name}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="p-4 border-t border-gray-200 bg-white flex justify-end space-x-3">
                    <button onClick={onClose} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">Cancel</button>
                    <button onClick={handleSave} disabled={isSaving} className="px-6 py-2.5 bg-[#20BEC6] text-white rounded-lg font-bold hover:opacity-90 shadow-sm">{isSaving ? 'Saving...' : 'Confirm Attendance'}</button>
                </div>
            </div>
        </div>
    );
}

function FeedbackModal({ appId, session, userId, onClose }) {
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault(); setIsSaving(true);
        try {
            await updateDoc(doc(db, `artifacts/${appId}/pd_events`, session.id), { [`feedbackLog.${userId}`]: { rating, comment, timestamp: new Date().toISOString() } });
            setIsSaving(false); onClose();
        } catch (err) { console.error(err); setIsSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                <h3 className="text-xl font-bold text-[#213368] mb-4">Rate This Session</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex justify-center space-x-2">{[1, 2, 3, 4, 5].map(s => <button key={s} type="button" onClick={() => setRating(s)} className={`text-4xl ${s <= rating ? 'text-yellow-400' : 'text-gray-200'}`}>★</button>)}</div>
                    <textarea className="w-full border border-gray-300 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none" rows="3" value={comment} onChange={e => setComment(e.target.value)} placeholder="Comments..." required />
                    <div className="flex justify-end space-x-3 pt-2"><button type="button" onClick={onClose} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button><button type="submit" disabled={isSaving} className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded-lg hover:opacity-90">Submit</button></div>
                </form>
            </div>
        </div>
    );
}

function ViewFeedbackModal({ session, onClose }) {
    const feedbackList = session.feedbackLog ? Object.values(session.feedbackLog) : [];
    const avg = feedbackList.length ? (feedbackList.reduce((a, b) => a + b.rating, 0) / feedbackList.length).toFixed(1) : 0;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
                <div className="p-6 border-b border-gray-100 flex justify-between"><h3 className="text-xl font-bold text-[#213368]">Feedback</h3><button onClick={onClose}><XIcon/></button></div>
                <div className="p-6 bg-gray-50 border-b border-gray-100 flex justify-center space-x-4"><div className="text-center"><span className="text-4xl font-extrabold text-[#213368]">{avg}</span></div><div className="text-3xl text-yellow-400">★</div><div className="text-center pl-4 border-l"><span className="text-xl font-bold text-gray-600">{feedbackList.length}</span></div></div>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">{feedbackList.length === 0 ? <p className="text-center text-gray-400">No feedback.</p> : feedbackList.map((i, idx) => <div key={idx} className="bg-white border p-4 rounded shadow-sm"><p className="text-sm italic">"{i.comment}"</p></div>)}</div>
            </div>
        </div>
    );
}